import type {
  ConfigEntityDetail,
  EntityDetailSchema,
} from '@web-config-app/core';

export interface RelationshipConfigEntityRef {
  id: string;
  domainEntityType?: string;
}

/**
 * Extracts the domain entity type for a relationship key from the entity schema.
 * Looks for the `enum` value on the `type` property within the relationship's data definition.
 *
 * Handles both array relationships (data.items.properties.type.enum) and
 * single-object relationships (data.properties.type.enum).
 */
function getDomainEntityTypeFromSchema(
  relationshipKey: string,
  schema?: EntityDetailSchema,
): string | undefined {
  // Navigate deeply nested schema structures using loose typing
  // since JSON Schema types can be complex unions of arrays and objects
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const relationshipSchema = (schema?.properties?.relationships as any)
    ?.properties?.[relationshipKey];
  if (!relationshipSchema) return undefined;

  const dataSchema = relationshipSchema?.properties?.data;
  if (!dataSchema) return undefined;

  // Array relationship: data.items.properties.type.enum
  const arrayTypeEnum = dataSchema?.items?.properties?.type?.enum;
  if (Array.isArray(arrayTypeEnum) && arrayTypeEnum.length > 0) {
    return arrayTypeEnum[0];
  }

  // Single-object relationship: data.properties.type.enum
  const objectTypeEnum = dataSchema?.properties?.type?.enum;
  if (Array.isArray(objectTypeEnum) && objectTypeEnum.length > 0) {
    return objectTypeEnum[0];
  }

  return undefined;
}

export function extractRelationshipConfigEntityRefs(
  entityData?: Partial<ConfigEntityDetail>,
  schema?: EntityDetailSchema,
): RelationshipConfigEntityRef[] {
  if (!entityData?.relationships) {
    return [];
  }

  return Object.entries(entityData.relationships).flatMap(
    ([relationshipKey, relationship]) => {
      // Try to get domain entity type from schema first, then fall back to data
      const schemaType = getDomainEntityTypeFromSchema(relationshipKey, schema);

      const relationData = relationship?.data;
      if (Array.isArray(relationData)) {
        return relationData
          .filter((item) => typeof item?.id === 'string')
          .map((item) => ({
            id: item.id,
            domainEntityType:
              schemaType ??
              (item.type && item.type !== 'config-entity'
                ? item.type
                : undefined),
          }));
      }
      if (relationData && typeof relationData === 'object') {
        if (typeof relationData.id === 'string') {
          return [
            {
              id: relationData.id,
              domainEntityType:
                schemaType ??
                (relationData.type && relationData.type !== 'config-entity'
                  ? relationData.type
                  : undefined),
            },
          ];
        }
      }
      return [];
    },
  );
}

export function extractRelationshipConfigEntityIds(
  entityData?: Partial<ConfigEntityDetail>,
): string[] {
  return extractRelationshipConfigEntityRefs(entityData).map((ref) => ref.id);
}
